import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { LUMIO_APP_ROUTE_PREFIX } from '@lumio/shared';
import { SessionMonitorService } from '../session/session-monitor.service';

interface SecurityErrorDto {
  code?: string;
  action?: string;
}

/**
 * Intercepts 403 responses with code=PROFILE_INCOMPLETE and redirects the user
 * to the complete-profile step instead of letting the error surface as a generic failure.
 * This is distinct from 401 handling (which lives in authInterceptor).
 */
export const profileIncompleteInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const sessionMonitor = inject(SessionMonitorService);

  return next(req).pipe(
    catchError((err: unknown): Observable<never> => {
      if (err instanceof HttpErrorResponse && err.status === 403) {
        // Never redirect based on auth-endpoint responses — those endpoints
        // handle their own error state (e.g. /auth/verify-email returning
        // EMAIL_NOT_VERIFIED would otherwise cause an infinite reload loop).
        const isAuthEndpoint = req.url.includes('/auth/');
        if (!isAuthEndpoint) {
          const body = err.error as SecurityErrorDto | null;
          if (body?.code === 'PROFILE_INCOMPLETE' && !req.url.includes('/users/preferences')) {
            void router.navigate([`${LUMIO_APP_ROUTE_PREFIX}/auth/complete-profile`], {
              replaceUrl: true,
            });
          } else if (
            body?.code === 'EMAIL_NOT_VERIFIED' || body?.code === 'EMAIL_VERIFICATION_REQUIRED'
          ) {
            void router.navigate([`${LUMIO_APP_ROUTE_PREFIX}/auth/verify-email`], {
              replaceUrl: true,
            });
          } else if (body?.code === 'ACCESS_DENIED') {
            // ACCESS_DENIED can mean either account deletion OR premium content restriction.
            // Verify session with /users/me before logging out:
            // - /users/me OK  → account exists, just a content restriction → propagate error to component
            // - /users/me 401/403 → account truly gone → logout
            return sessionMonitor.handleFailedAuthenticatedRequest(err, req);
          }
        }
      }
      return throwError(() => err);
    }),
  );
};
